import type { VenueId } from "@orderman/shared";
import { settings, updateSettings, getDeviceId, setDeviceName, setDeviceOwnerClass, deviceUnassigned, setResetGen } from "./config.js";
import { relayBase, initStore, menu } from "./store.js";
import { users, currentUser } from "./auth.js";

// deviceUnassigned lebt in config.ts (zyklusfrei); hier nur re-exportiert für App/Login.
export { deviceUnassigned };

/**
 * Remote-Scharfschalten (zweiter Trigger neben QR): ein im Inventar registriertes Handy
 * fragt die Cloud nach seiner aktuellen Zuweisung (assignment.php, keyed über die eigene
 * deviceId). Ändert der Betreiber die Zuweisung (andere Veranstaltung → höhere Epoche),
 * übernimmt das Gerät die neue Venue und re-initialisiert sich — bei Venue-Wechsel greift
 * der v1-Reset (reconcileVenue: sync-before-wipe). Nicht-registrierte Geräte: found=false → no-op.
 */

const EPOCH_KEY = "assign.epoch"; // zuletzt übernommene Epoche (verhindert Re-Trigger)

export async function pollAssignment(): Promise<void> {
  try {
    const res = await fetch(`${relayBase()}/assignment.php?deviceId=${encodeURIComponent(getDeviceId())}`);
    if (!res.ok) return;
    const j = (await res.json()) as { found?: boolean; venueId?: string | null; epoch?: number; name?: string; ownerClass?: string; resetGen?: number };
    if (j.found && j.name) setDeviceName(j.name); // Gerätename fürs Login-Display auffrischen
    if (j.found) setDeviceOwnerClass(j.ownerClass); // Herkunft auffrischen (betreiber/byd)

    if (!j.found || !j.venueId) {
      // (Noch) keiner Veranstaltung zugewiesen: auf sauberem Login bleiben, KEIN Personal/keine
      // Karte vom letzten Event stehen lassen. Einmalig beim Übergang leeren + ausloggen.
      if (!deviceUnassigned.value) {
        deviceUnassigned.value = true;
        currentUser.value = null;
        users.value = [];
        menu.value = null;
      }
      return;
    }
    const wasUnassigned = deviceUnassigned.value;
    deviceUnassigned.value = false; // hat eine Venue → normaler Betrieb

    const storedEpoch = Number(localStorage.getItem(EPOCH_KEY) ?? "-1");
    const epoch = Number(j.epoch ?? 0);
    const venueChanged = j.venueId !== settings.value.venueId;
    // Auch wenn Venue/Epoche gleich sind: war das Gerät gerade noch „nicht zugewiesen", wurden
    // Personal/Karte geleert → jetzt neu laden (sonst bliebe der Login ohne Personal).
    if (!venueChanged && epoch === storedEpoch && !wasUnassigned) return; // nichts Neues

    localStorage.setItem(EPOCH_KEY, String(epoch));
    if (venueChanged) updateSettings({ venueId: j.venueId as VenueId });
    // reset-Generation der (neuen) Venue seeden, BEVOR initStore/erste Bestellung läuft. Ohne das
    // startet ein frisch zugewiesenes Gerät bei gen 0; die erste Bestellung pusht gen 0 gegen eine
    // bereits einmal zurückgesetzte Venue → 409 → die neue Bestellung würde als stale verworfen.
    // Wir sind hier nur bei einem echten Zuweisungs-Übergang (Zeile 46 filtert Steady-State-Polls),
    // also überschreibt das keinen laufenden Reset-Zustand.
    if (typeof j.resetGen === "number" && j.venueId) setResetGen(j.venueId, j.resetGen);
    // Re-Init: reconcileVenue erkennt den Venue-Wechsel und fährt den v1-Reset (oder blockt,
    // wenn noch un-gesynchte Bons der alten Venue offen sind).
    await initStore();
  } catch {
    /* offline / Cloud weg → später erneut */
  }
}
